import {
  Text,
  Button,
  Box,
  Flex,
  useToast,
  Spacer,
  useDisclosure,
} from '@chakra-ui/react';
import { getIn, setIn } from 'formik';
import * as _ from 'lodash';
import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { date as commonDate } from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import { WipConfig } from '../../types';
import { AddPortfolioCovenantModal } from '../entity/portfolioCovenant/AddPortfolioCovenantModal';
import { PortfolioCovenantTable } from '../entity/portfolioCovenant/PortfolioCovenantTable';
import {
  ResetIconButton,
  SaveIconButton,
} from 'packages/common-frontend/components/IconButton';
import { Icon } from 'packages/common-frontend/components/icons';
import { SimpleDatePicker } from 'packages/common-frontend/components/SimpleDatePicker';
import { StickyHeaderSection } from 'packages/common-frontend/components/StickyHeaderSection';
import { useEvaluatePortfolioCovenantsMutation } from 'packages/creditAgreements/requests/requests.gql.generated';
import { CreditAgreement } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { getErrorMessageFromApolloError } from '~/contexts/ApolloClientProvider';

const createPortfolioCovenantPropertyShell = ({
  covenantNameInput,
  covenantKeyInput,
}: {
  covenantNameInput: string;
  covenantKeyInput: string;
}): CreditAgreement.DomainEntity.PortfolioCovenantProperty => ({
  name: covenantNameInput,
  covenantKey: covenantKeyInput,
  type: CreditAgreement.DomainEntity.PropertyType.PortfolioCovenantProperty,
  value: 'description of covenant',
  _isWrittenIn_: true,
});

type PortfolioCovenantsProps = {
  portfolioCovenants: CreditAgreement.DomainEntity.PortfolioCovenantProperty[];
  wipConfig?: WipConfig;
  organizationId: string;
};
export const PortfolioCovenants = ({
  portfolioCovenants,
  wipConfig,
  organizationId,
}: PortfolioCovenantsProps) => {
  const toast = useToast();
  const navigate = useNavigate();
  const {
    isOpen: isAddModalOpen,
    onOpen: onAddModalOpen,
    onClose: onAddModalClose,
  } = useDisclosure();

  const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const basePropertyKey = 'portfolioCovenants';

  const [triggerDate, setTriggerDate] = useState<Util.FullDateStr>(
    commonDate.dateToFullDateString(new Date(), clientTimeZone),
  );
  const [isAddLoading, setIsAddLoading] = useState<boolean>(false);

  const isClientWipMode = wipConfig?.operationMode === 'client';

  const [
    evaluatePortfolioCovenants,
    { loading: isEvaluatePortfolioCovenantsQALoading },
  ] = useEvaluatePortfolioCovenantsMutation({
    onCompleted: () => {
      toast({
        title:
          'Initiated portfolio covenants evaluation based on latest SAVED WIP.  Please check your email in a few minutes.',
        duration: 3000,
        status: 'success',
        position: 'top',
      });
    },
    onError: (error) => {
      toast({
        title: getErrorMessageFromApolloError({
          error,
          defaultMessage:
            'Unable to initiate portfolio covenants evaluation. Please see technical assistance.',
        }),
        isClosable: true,
        status: 'error',
        position: 'top',
      });
    },
  });

  const handleEvaluatePortfolioCovenantsQA = useCallback(
    async (wc: WipConfig) => {
      const creditAgreementId = getIn(wc.formProps.values, 'id');
      const creditFacilityId = getIn(wc.formProps.values, 'creditFacilityId');
      const portCovs = getIn(
        wc.formProps.values,
        basePropertyKey,
      ) as CreditAgreement.DomainEntity.PortfolioCovenantProperty[];
      const covenantKeys = portCovs
        .filter((portCov) => !!portCov._config_)
        .map((portCov) => portCov.covenantKey);

      if (covenantKeys.length) {
        await evaluatePortfolioCovenants({
          variables: {
            evaluatePortfolioCovenantsRequest: {
              covenantKeys,
              creditAgreementId,
              creditFacilityId,
              triggerDate,
              mode: 'QA',
            },
          },
        });
      } else {
        toast({
          title: 'No portfolioCovenant configs exist to QA.',
          status: 'warning',
          isClosable: true,
          duration: 5000,
          position: 'top',
        });
      }
    },
    [evaluatePortfolioCovenants, triggerDate, toast],
  );

  const addPortfolioCovenant = useCallback(
    async ({
      newName,
      newCovenantKey,
    }: {
      newName: string;
      newCovenantKey: string;
    }): Promise<void> => {
      setIsAddLoading(true);
      if (!wipConfig?.isMutateAllowed(basePropertyKey)) {
        return;
      }

      const newPortfolioCovenant = createPortfolioCovenantPropertyShell({
        covenantNameInput: newName,
        covenantKeyInput: newCovenantKey,
      });

      const existingPortfolioCovenants = getIn(
        wipConfig.formProps.values,
        basePropertyKey,
      );

      const newPortfolioCovenants = [
        ...existingPortfolioCovenants,
        newPortfolioCovenant,
      ];

      const updatedWipCreditAgreement = setIn(
        wipConfig.formProps.values,
        basePropertyKey,
        newPortfolioCovenants,
      );

      const saveData = await wipConfig.saveCreditAgreementFullArtifactWip({
        variables: {
          creditAgreementFullArtifactWip: {
            id: wipConfig.formProps.values.id,
            creditFacilityId: wipConfig.formProps.values.creditFacilityId,
            creditAgreement: JSON.stringify(updatedWipCreditAgreement),
          },
        },
        notifyOnNetworkStatusChange: true,
      });

      if (saveData.data) {
        setIsAddLoading(false);
        onAddModalClose();

        if (isClientWipMode) {
          const creditFacilityId = getIn(
            wipConfig.formProps.values,
            'creditFacilityId',
          );

          // if successfully able to save new portcov, redirect to edit page for the new portcov
          navigate(
            `/settings/credit-facilities/${creditFacilityId}/portfolio-covenants/edit?covenantKey=${newCovenantKey}`,
          );
        } else {
          wipConfig.formProps.setFieldValue(
            basePropertyKey,
            newPortfolioCovenants,
          );
        }
      } else {
        // if CA could not save, allow user to edit inside the modal and try again
        setIsAddLoading(false);
      }
    },
    [basePropertyKey, navigate, wipConfig, onAddModalClose, isClientWipMode],
  );

  if (
    !wipConfig?.isMutateAllowed(basePropertyKey) &&
    !_.compact(Object.values(portfolioCovenants)).length
  ) {
    return null;
  }

  return (
    <Box mt="40px">
      <StickyHeaderSection
        labelText={!isClientWipMode ? 'Portfolio Covenants' : undefined}
      >
        {wipConfig?.isMutateAllowed(basePropertyKey) && !isClientWipMode && (
          <Flex
            alignItems="start"
            border="1px"
            borderColor="gray.700"
            p="3"
            mb="40px"
          >
            <Text fontStyle="italic" mr="3" pt="0.5rem">
              &quot;triggerDate&quot; mock:
            </Text>
            <SimpleDatePicker
              selectedDate={commonDate.fullDateStrToDate(triggerDate)}
              setSelectedDate={(date: Date | null) => {
                if (!date) return;
                const fullDateStr = commonDate.dateToFullDateString(
                  date,
                  clientTimeZone,
                );
                setTriggerDate(fullDateStr);
              }}
            />
            <SaveIconButton
              ariaLabel="save config"
              label="Save Configuration"
              onClick={() => {
                wipConfig.saveCreditAgreementFullArtifactWip({
                  variables: {
                    creditAgreementFullArtifactWip: {
                      id: wipConfig.formProps.values.id,
                      creditFacilityId:
                        wipConfig.formProps.values.creditFacilityId,
                      creditAgreement: JSON.stringify(
                        wipConfig.formProps.values,
                      ),
                    },
                  },
                });
              }}
              ml="2"
            />
            <ResetIconButton
              ariaLabel="qa portfolio covenants"
              label="QA Portfolio Covenants"
              onClick={() => handleEvaluatePortfolioCovenantsQA(wipConfig)}
              isDisabled={isEvaluatePortfolioCovenantsQALoading}
            />
          </Flex>
        )}

        <Flex alignItems="center" mb="40px">
          {wipConfig?.isMutateAllowed(basePropertyKey) && (
            <>
              <Spacer />
              <Button
                aria-label="Add Portfolio Covenant"
                minW="fit-content"
                borderWidth="2px"
                colorScheme="v2-aurora"
                variant="secondary"
                onClick={() => {
                  onAddModalOpen();
                }}
              >
                <Icon icon="plus" />
                <Text ml="16px">Add Portfolio Covenant</Text>
              </Button>
              <AddPortfolioCovenantModal
                isOpen={isAddModalOpen}
                onClose={onAddModalClose}
                onClickAdd={({
                  nameInput,
                  covenantKeyInput,
                }: {
                  nameInput: string;
                  covenantKeyInput: string;
                }) =>
                  addPortfolioCovenant({
                    newName: nameInput,
                    newCovenantKey: covenantKeyInput,
                  })
                }
                initialCovenantKey="portfolioCovenantKey"
                initialName="Portfolio Covenant Name"
                isAddLoading={isAddLoading}
                mode="add"
              />
            </>
          )}
        </Flex>

        <PortfolioCovenantTable
          organizationId={organizationId}
          portfolioCovenants={portfolioCovenants}
          basePropertyKey={basePropertyKey}
          wipConfig={wipConfig}
          triggerDate={triggerDate}
        />
      </StickyHeaderSection>
    </Box>
  );
};
